import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../../../../../../ui/toast';
import { NS, ORG_SETTINGS_KEYS } from '../../../../../../i18n/constants/keys';
import { Button } from '../../../../../../ui';

const DEFAULT_LOGO = '/logo.png'; // Default HiAcuity logo path

type UploadBase64ImageProps = {
  logo: string;
  setLogo: (base64Logo: string) => void;
  isEditing: boolean;
};

export const UploadBase64Image = ({
  logo,
  setLogo,
  isEditing,
}: UploadBase64ImageProps) => {
  const { t } = useTranslation(NS.organizationSettings);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const validateFile = async (
    file: File,
  ): Promise<{ isValid: boolean; error?: string }> => {
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Reset previous errors
    setUploadError(null);

    if (!validTypes.includes(file.type)) {
      const error = t(ORG_SETTINGS_KEYS.logo.invalidFileType);
      setUploadError(error);
      return { isValid: false, error };
    }

    if (file.size > maxSize) {
      const error = t(ORG_SETTINGS_KEYS.logo.fileTooLarge);
      setUploadError(error);
      return { isValid: false, error };
    }

    return { isValid: true };
  };

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setIsLoading(true);

    try {
      const file = e.dataTransfer.files[0];
      const validation = await validateFile(file);

      if (!validation.isValid) {
        toast.error(validation.error, { position: 'bottom-center' });
        return;
      }

      const dataUrl = await readFileAsDataURL(file);
      setLogo(dataUrl);
      setUploadError(null);
      toast.success(t(ORG_SETTINGS_KEYS.logo.uploadSuccess), {
        position: 'bottom-center',
      });
    } catch (error) {
      toast.error(t(ORG_SETTINGS_KEYS.logo.uploadFailed), {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsLoading(true);

      try {
        const file = e.target.files[0];
        const validation = await validateFile(file);

        if (!validation.isValid) {
          toast.error(validation.error, { position: 'bottom-center' });
          return;
        }

        const dataUrl = await readFileAsDataURL(file);
        setLogo(dataUrl);
        setUploadError(null);
        toast.success(t(ORG_SETTINGS_KEYS.logo.uploadSuccess), {
          position: 'bottom-center',
        });
      } catch (error) {
        toast.error(t(ORG_SETTINGS_KEYS.logo.uploadFailed), {
          position: 'bottom-center',
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <motion.div
      layout
      className={`flex flex-col justify-center items-center w-full rounded-2xl p-5 transition-all duration-300 ${
        isDragging
          ? 'ring-2 ring-emerald-500 bg-emerald-50/50'
          : 'bg-white/80 border border-slate-200/80 shadow-sm'
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        type='file'
        className='hidden'
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept='.jpg,.jpeg,.png,.svg'
      />
      {logo ? (
        <div className='flex flex-col gap-5 w-full'>
          <div
            className='relative group overflow-hidden rounded-2xl flex items-center justify-center h-20 shadow-inner transition-colors duration-300'
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <div className='absolute inset-0 bg-gradient-to-t from-black/10 to-white/10 pointer-events-none' />
            {isLoading ? (
              <div className='flex items-center justify-center'>
                <div className='animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent' />
              </div>
            ) : (
              <img
                src={logo || DEFAULT_LOGO}
                alt={t(ORG_SETTINGS_KEYS.logo.companyLogoAlt)}
                className={`max-w-[170px] max-h-[46px] object-contain mx-auto transition-all duration-300 drop-shadow-sm ${
                  !logo ? 'opacity-75' : ''
                }`}
              />
            )}
          </div>

          <div className='space-y-3.5'>
            <div className='flex flex-col w-full gap-3'>
              <p className='text-slate-500 text-xs font-semibold'>
                {t(ORG_SETTINGS_KEYS.labels.logoPreview)}
              </p>

              {isEditing && (
                <>
                  <div className='flex items-center gap-2'>
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type='button'
                      onClick={handleClick}
                      className='flex-1 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm transition-all'
                    >
                      {t(ORG_SETTINGS_KEYS.logo.changeLogo)}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type='button'
                      onClick={() => setLogo('')}
                      className='px-3 py-2 text-xs font-semibold rounded-xl border border-rose-200 bg-rose-50/50 text-rose-600 hover:bg-rose-100/60 transition-all'
                    >
                      {t(ORG_SETTINGS_KEYS.logo.remove)}
                    </motion.button>
                  </div>

                  <div className='flex items-center gap-2 pt-1'>
                    <input
                      type='checkbox'
                      id='useDefaultLogo'
                      checked={!logo}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLogo(DEFAULT_LOGO);
                        }
                      }}
                      className='h-4 w-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer'
                    />
                    <label
                      htmlFor='useDefaultLogo'
                      className='text-xs font-medium text-slate-600 flex items-center gap-2 cursor-pointer select-none'
                    >
                      {t(ORG_SETTINGS_KEYS.logo.useDefaultCheckbox)}
                      <img
                        src={DEFAULT_LOGO}
                        alt={t(ORG_SETTINGS_KEYS.logo.defaultLogoAlt)}
                        className='h-3.5 opacity-75'
                      />
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-4 py-4 px-2 text-center w-full'>
          <div className='w-14 h-14 rounded-2xl bg-emerald-50/80 flex items-center justify-center shadow-inner'>
            <svg
              className='w-7 h-7 text-emerald-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>

          <div className='space-y-3 w-full'>
            <p className='text-slate-800 font-bold text-xs'>
              {t(ORG_SETTINGS_KEYS.logo.dragDrop)}
            </p>

            {uploadError && (
              <div className='text-red-600 text-xs bg-red-50 p-2.5 rounded-xl border border-red-200'>
                {uploadError}
              </div>
            )}

            <div className='bg-slate-50/80 rounded-xl p-3 border border-slate-100 text-left text-[11px] text-slate-500 space-y-1.5'>
              <div className='font-bold text-slate-700 uppercase tracking-wider text-[10px]'>
                {t(ORG_SETTINGS_KEYS.logo.requirementsTitle)}
              </div>
              <ul className='space-y-1'>
                <li className='flex items-center gap-1.5'>
                  <span className='text-emerald-500'>✓</span>
                  <span>{t(ORG_SETTINGS_KEYS.logo.formatRequirement)}</span>
                </li>
                <li className='flex items-center gap-1.5'>
                  <span className='text-emerald-500'>✓</span>
                  <span>{t(ORG_SETTINGS_KEYS.logo.dimensionRequirement)}</span>
                </li>
                <li className='flex items-center gap-1.5'>
                  <span className='text-emerald-500'>✓</span>
                  <span>{t(ORG_SETTINGS_KEYS.logo.fileSizeRequirement)}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className='flex flex-col gap-2.5 w-full pt-1'>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type='button'
              onClick={handleClick}
              className='w-full py-2 px-3 text-xs font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 shadow-sm transition-all'
            >
              {t(ORG_SETTINGS_KEYS.logo.uploadCustom)}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type='button'
              onClick={() => setLogo(DEFAULT_LOGO)}
              className='w-full py-2 px-3 text-xs font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all'
            >
              {t(ORG_SETTINGS_KEYS.logo.useDefaultButton)}
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};
